import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { makeRootVariableNode, toVariableEntities, type VariableEntity, type VariableNode } from 'services/entities/VariablesEntity';
import { useCombinedVariablesQueries } from 'services/variables/useVariablesQuery';
import type { GetDocumentResponse } from 'services/documents/DocumentRepository';
import { updateTreeNode } from '@proposify/frontend-utils/updateTreeNode';
import { useFeatureFlagAnonymous } from 'hooks/useFeatureFlagAnonymous';
import { FeatureFlags } from 'utils/featureFlags';

function applyUpdateToTree(tree: VariableNode, variableName: VariableEntity['name'], newValue: VariableEntity['value']): VariableNode {
  if (!tree.variables || tree.variables.length === 0) return tree;

  let hasUpdated = false;
  const searcher = (node: VariableNode) => !hasUpdated && node.name === variableName;
  const updater = (node: VariableNode) => {
    hasUpdated = true;
    return { ...node, value: newValue };
  };

  const nextVariables = tree.variables.map((rootNode) => updateTreeNode(rootNode, searcher, updater, 'variables'));

  return hasUpdated ? { ...tree, variables: nextVariables } : tree;
}

export type PreviewVariablesContextType = {
  variables: VariableEntity[];
  updateVariable: (variableName: VariableEntity['name'], newValue: VariableEntity['value']) => void;
  crmPricingEmbedEnabled?: boolean;
};

export const PreviewVariablesContext = createContext<PreviewVariablesContextType | null>(null);

interface PreviewVariablesProviderProps {
  children: React.ReactNode;
  documentData: GetDocumentResponse | undefined;
}

export const PreviewVariablesProvider = ({ children, documentData }: PreviewVariablesProviderProps) => {
  const contentId = documentData?.contentId;
  const [variables, setVariables] = useState<VariableNode>(() => makeRootVariableNode());
  const pendingUpdatesRef = useRef(new Map<VariableEntity['name'], VariableEntity['value']>());
  const crmAnonymousFlagKeys = [FeatureFlags.crmPricingEmbed, FeatureFlags.v2VariableRoutes];
  const { flags: anonymousFlags } = useFeatureFlagAnonymous(crmAnonymousFlagKeys);
  const crmPricingEmbedEnabled = anonymousFlags[FeatureFlags.crmPricingEmbed] === true;
  const customFieldShapeEnabled = anonymousFlags[FeatureFlags.v2VariableRoutes] === true;
  const { flags: appNativeAnonymousFlags, isError: appNativeAnonymousFlagsError } = useFeatureFlagAnonymous([
    FeatureFlags.appNativeVariablesV2Route,
  ]);
  const appNativeVariablesV2RouteEnabled = appNativeAnonymousFlags[FeatureFlags.appNativeVariablesV2Route] === true;
  // `useFeatureFlagAnonymous`'s query never rejects today (every failure resolves to `false`
  // instead), so `isError` can't currently fire here — checked anyway so a future change to that
  // contract can't leave this gate stuck open on a real error instead of releasing it.
  const appNativeVariablesV2RouteLoading =
    !appNativeAnonymousFlagsError && appNativeAnonymousFlags[FeatureFlags.appNativeVariablesV2Route] === undefined;
  const { data: combinedVariables } = useCombinedVariablesQueries(contentId ?? '', 'preview', {
    customFieldShapeEnabled,
    appNativeVariablesV2Route: { enabled: appNativeVariablesV2RouteEnabled, isLoading: appNativeVariablesV2RouteLoading },
  });
  useEffect(() => {
    let nextTree = combinedVariables;
    pendingUpdatesRef.current.forEach((newValue, variableName) => {
      nextTree = applyUpdateToTree(nextTree, variableName, newValue);
    });
    setVariables(nextTree);
  }, [combinedVariables]);

  const value = useMemo(() => {
    const updateVariable = (variableName: VariableEntity['name'], newValue: VariableEntity['value']) => {
      pendingUpdatesRef.current.set(variableName, newValue);
      setVariables((current) => applyUpdateToTree(current, variableName, newValue));
    };

    return {
      variables: toVariableEntities(variables),
      updateVariable,
      crmPricingEmbedEnabled,
    };
  }, [variables, crmPricingEmbedEnabled]);

  return <PreviewVariablesContext.Provider value={value}>{children}</PreviewVariablesContext.Provider>;
};

export const usePreviewVariables = () => {
  const variablesContext = useContext(PreviewVariablesContext);
  if (!variablesContext) {
    throw new Error('usePreviewContentVariables must be used within a PreviewVariablesProvider');
  }
  return variablesContext;
};
